import { t } from 'i18next';
import { useCurrencyContext } from '@/contexts/CurrencyContext';
import useAggregateTakerMarketStats from '@/enterprise/data/useAggregateTakerMarketStats';
import useClosedMarkets from '@/enterprise/data/useClosedMarkets';
import useTakerMarkets from '@/enterprise/data/useTakerMarkets';
import useLocaleFormat from '@/utils/useLocaleFormat';
import { AcceptanceCardType } from './AcceptanceCard';

const useAcceptanceCardDetails = (cardType: AcceptanceCardType) => {
  const { selectedCurrency } = useCurrencyContext();
  const { data: takerMarkets } = useTakerMarkets();
  const takerMarketStats = useAggregateTakerMarketStats(takerMarkets);
  const { asCurrency, asNumber } = useLocaleFormat();
  const { getClosedMarkets } = useClosedMarkets();
  const closedMarkets = getClosedMarkets(takerMarkets, 'eligible');

  const eligibleInvoiceAmountSelectedCurrency =
    takerMarketStats?.byCurrency[selectedCurrency].eligibleInvoiceAmount ?? 0;

  // calculate accepted invoice amount and percentage of total
  const clearingInvoiceAmountSelectedCurrency =
    takerMarketStats?.byCurrency[selectedCurrency].clearingInvoiceAmount ?? 0;

  // calculate not accepted invoice amount and percentage of total
  const totalNotAcceptedAmountSelectedCurrency =
    takerMarketStats?.byCurrency[selectedCurrency].notAcceptedInvoiceAmount ?? 0;

  // create card props based on card type
  switch (cardType) {
    case 'included': {
      return {
        acceptanceText: t('core.includedAmount'),
        totalInvoiceAmountSelectedCurrency: asCurrency(eligibleInvoiceAmountSelectedCurrency, selectedCurrency),
        cardType: 'included',
        closedMarketCount: closedMarkets.closedMarketCount,
        closedInvoiceAmount: asCurrency(closedMarkets.closedInvoiceAmount, selectedCurrency),
        hasClosedMarkets: closedMarkets.hasClosedMarkets,
        displayCard: true,
      };
    }

    // this card contains information for invoices that are currently accepted
    case 'likelyToBeAccepted': {
      const clearingInvoiceDivisionCount = takerMarketStats?.aggregateStats.clearingInvoiceDivisionCount ?? 0;
      const clearingEarnSelectedCurrency = takerMarketStats?.byCurrency[selectedCurrency].clearingEarn ?? 0;
      const clearingInvoiceCount = takerMarketStats?.aggregateStats.clearingInvoiceCount ?? 0;
      const clearingDpeWeightedAvg = takerMarketStats?.byCurrency[selectedCurrency].clearingDpeWeightedAvg ?? 0;

      const clearingInvoiceDivisionText =
        clearingInvoiceDivisionCount === 1
          ? t('core.divisionSelected', {
              selectedDivisionCount: asNumber({
                value: clearingInvoiceDivisionCount,
              }),
            })
          : t('core.divisionsSelected', {
              selectedDivisionCount: asNumber({
                value: clearingInvoiceDivisionCount,
              }),
            });
      const clearingInvoiceCountText =
        clearingInvoiceCount === 1
          ? `${asNumber({ value: clearingInvoiceCount })} ${t('core.invoice')}`
          : `${asNumber({ value: clearingInvoiceCount })} ${t('core.invoicePlural')}`;
      const clearingDpeWeightedAvgText = `${asNumber({
        value: clearingDpeWeightedAvg,
      })} ${t('core.daysPaidEarly')}`;

      return {
        acceptanceText: t('homePageBanner.likelyToBeAccepted'),
        discountAmountSelectedCurrency: `${asCurrency(clearingEarnSelectedCurrency, selectedCurrency)} ${t(
          'core.discount'
        )}`,
        totalInvoiceAmountSelectedCurrency: asCurrency(clearingInvoiceAmountSelectedCurrency, selectedCurrency),
        cardType: 'likelyToBeAccepted',
        numberOfDivisionsText: clearingInvoiceDivisionText,
        daysPaidEarlyText: clearingDpeWeightedAvgText,
        numberOfInvoicesText: clearingInvoiceCountText,
        displayCard: clearingInvoiceCount > 0,
      };
    }

    case 'unlikelyToBeAccepted': {
      // this card contains information for invoices that are not currently accepted
      const notAcceptedDivisionCount = takerMarketStats?.aggregateStats.notAcceptedInvoiceDivisionCount ?? 0;
      const notAcceptedDivisionCountText =
        notAcceptedDivisionCount === 1
          ? t('core.divisionSelected', {
              selectedDivisionCount: asNumber({ value: notAcceptedDivisionCount }),
            })
          : t('core.divisionsSelected', {
              selectedDivisionCount: asNumber({ value: notAcceptedDivisionCount }),
            });
      const notAcceptedInvoiceCount = takerMarketStats?.aggregateStats.notAcceptedInvoiceCount ?? 0;
      const notAcceptedInvoiceCountText =
        notAcceptedInvoiceCount === 1
          ? `${asNumber({ value: notAcceptedInvoiceCount })} ${t('core.invoice')}`
          : `${asNumber({ value: notAcceptedInvoiceCount })} ${t('core.invoicePlural')}`;
      const notAcceptedDpeWeightedAvgText = `${asNumber({
        value: takerMarketStats?.byCurrency[selectedCurrency].notAcceptedDpeWeightedAvg ?? 0,
      })} ${t('core.daysPaidEarly')}`;

      return {
        acceptanceText: t('homePageBanner.unlikelyToBeAccepted'),
        totalInvoiceAmountSelectedCurrency: asCurrency(totalNotAcceptedAmountSelectedCurrency, selectedCurrency),
        cardType: 'unlikelyToBeAccepted',
        numberOfDivisionsText: notAcceptedDivisionCountText,
        daysPaidEarlyText: notAcceptedDpeWeightedAvgText,
        numberOfInvoicesText: notAcceptedInvoiceCountText,
        displayCard: notAcceptedInvoiceCount > 0,
      };
    }

    case 'nothingOffered': {
      // this card contains information for invoices that are not currently participating
      const totalNotParticipatingAmountSelectedCurrency =
        takerMarketStats?.byCurrency[selectedCurrency].notParticipatingInvoiceAmount ?? 0;
      const notParticipatingDivisionCount = takerMarketStats?.aggregateStats.notParticipatingDivisionCount ?? 0;
      const notParticipatingDivisionCountText =
        notParticipatingDivisionCount === 1
          ? t('core.divisionSelected', {
              selectedDivisionCount: asNumber({ value: notParticipatingDivisionCount }),
            })
          : t('core.divisionsSelected', {
              selectedDivisionCount: asNumber({ value: notParticipatingDivisionCount }),
            });
      const notParticipatingInvoiceCount = takerMarketStats?.aggregateStats.notParticipatingInvoiceCount ?? 0;
      const notParticipatingInvoiceCountText =
        notParticipatingInvoiceCount === 1
          ? `${asNumber({ value: notParticipatingInvoiceCount })} ${t('core.invoice')}`
          : `${asNumber({ value: notParticipatingInvoiceCount })} ${t('core.invoicePlural')}`;
      const notParticipatingDpeWeightedAvgText = `${asNumber({
        value: takerMarketStats?.byCurrency[selectedCurrency].notParticipatingDpeWeightedAvg ?? 0,
      })} ${t('core.daysPaidEarly')}`;

      return {
        acceptanceText: t('homePageBanner.nothingOffered'),
        totalInvoiceAmountSelectedCurrency: asCurrency(totalNotParticipatingAmountSelectedCurrency, selectedCurrency),
        cardType: 'nothingOffered',
        numberOfDivisionsText: notParticipatingDivisionCountText,
        daysPaidEarlyText: notParticipatingDpeWeightedAvgText,
        numberOfInvoicesText: notParticipatingInvoiceCountText,
        displayCard: notParticipatingInvoiceCount > 0,
      };
    }

    default:
      null;
  }
};

export default useAcceptanceCardDetails;
